import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import GeneralDropdownsTriggersTriggerPowerSelectKebab from 'lumen/components/general/dropdowns/triggers/trigger-power-select-kebab';
import SettingsCurrencyConversionModalsDeleteProfile from 'lumen/components/settings/currency-conversion/modals/delete-profile';
import SettingsCurrencyConversionModalsEditTitle from 'lumen/components/settings/currency-conversion/modals/edit-title';
import type { CurrencyConversionExchangeRatesProfile } from 'lumen/models';

export const ACTION_TYPE = {
  EDIT: 'edit',
  DELETE: 'delete',
  HELP: 'help',
} as const;

export type ActionType = (typeof ACTION_TYPE)[keyof typeof ACTION_TYPE];

export type SettingsCurrencyConversionDropdownMappingMoreActionsOptionType =
  Option & {
    action: ActionType;
    icon?: string;
    label: string;
    unsafe_isLink?: boolean;
  };

interface SettingsCurrencyConversionDropdownsMoreActionsSignature {
  Args: {
    exchangeRatesProfile: CurrencyConversionExchangeRatesProfile;
  };
}

export default class SettingsCurrencyConversionDropdownMoreActions extends Component<SettingsCurrencyConversionDropdownsMoreActionsSignature> {
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked isDeleteModalOpen = false;
  @tracked isEditTitleModalOpen = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dropdownOptions() {
    return [
      {
        action: ACTION_TYPE.EDIT,
        label: this.intl.t('settings.currency_conversion.actions.edit_title'),
      },
      {
        action: ACTION_TYPE.DELETE,
        label: this.intl.t(
          'settings.currency_conversion.action.delete_profile',
        ),
        icon: 'bin' as const,
      },
      {
        action: ACTION_TYPE.HELP,
        label: this.intl.t('general.help_center'),
        icon: 'book' as const,
        unsafe_isLink: true,
      },
    ];
  }

  @action closeDeleteModal(): void {
    this.isDeleteModalOpen = false;

    // Check if the profile was deleted (isDeleted will be true after successful deletion)
    if (this.args.exchangeRatesProfile.isDeleted) {
      const indexRoute = this.currencyConversion.isHotelLevel
        ? 'hotel.settings.currency-conversion.index'
        : 'market.settings.currency-conversion.index';

      this.router.transitionTo(indexRoute);
    }
  }

  @action closeEditTitleModal(): void {
    this.isEditTitleModalOpen = false;
  }

  @action onSelect(
    value: SettingsCurrencyConversionDropdownMappingMoreActionsOptionType,
  ): void {
    switch (value.action) {
      case ACTION_TYPE.EDIT:
        this.isEditTitleModalOpen = true;
        break;
      case ACTION_TYPE.DELETE:
        this.isDeleteModalOpen = true;
        break;
      case ACTION_TYPE.HELP:
        // Handle help action if needed
        break;
    }
  }

  <template>
    <PrismComboSelectInput
      @horizontalPosition="right"
      @onChange={{this.onSelect}}
      @options={{this.dropdownOptions}}
      @unsafe_triggerComponent={{GeneralDropdownsTriggersTriggerPowerSelectKebab}}
      data-test-id="currency-conversion-more-actions"
    />

    {{#if this.isEditTitleModalOpen}}
      <SettingsCurrencyConversionModalsEditTitle
        @activeExchangeRatesProfile={{@exchangeRatesProfile}}
        @onClose={{this.closeEditTitleModal}}
      />
    {{/if}}

    {{#if this.isDeleteModalOpen}}
      {{#if @exchangeRatesProfile}}
        <SettingsCurrencyConversionModalsDeleteProfile
          @exchangeRatesProfile={{@exchangeRatesProfile}}
          @onClose={{this.closeDeleteModal}}
        />
      {{/if}}
    {{/if}}
  </template>
}
